import {useQueryClient} from '@tanstack/react-query';
import {useState} from 'react';
import getContentSubmissionAnalysisButtonState from '@/utils/getContentSubmissionAnalysisButtonState';
import {raiseFlashMessageError} from '@/utils/raiseFlashMessage';
import {checkResizeEligibility} from '@/utils/resizeEligibility';
import translate from '@/utils/translate';
import {
  CONTENT_SUBMISSION_ACTIONS_BY_STATE,
  INACTIVE_CAMPAIGN_CONTENT_SUBMISSION_ACTIONS,
} from '@/config/contentSubmissionActions';
import {ACTIVE_CAMPAIGN_STATES, CampaignAasmState} from '@/types/campaign';
import {
  ContentSubmission,
  ContentSubmissionVersion,
  CONTENT_SUBMISSION_KIND,
  CONTENT_SUBMISSION_STATUS,
} from '@/types/contentSubmission';
import {
  CONTENT_SUBMISSION_ACTION,
  type ContentSubmissionAction,
} from '@/components/content-submission-card';
import useResizeAnalytics from '@/hooks/analytics/use-resize-analytics';
import {CONTENT_SUBMISSION_QUERY_KEY} from '@/hooks/queries/use-content-submission-query';
import useContentSubmissionAction from '@/hooks/use-content-submission-action';
import usePostProductionGenerations from '@/hooks/use-post-production-generations';

export type ResizeModalState = 'closed' | 'picker' | 'limit';

type UseContentSubmissionActionsFooterStateParams = {
  contentSubmission: ContentSubmission;
  version: ContentSubmissionVersion;
  isAdmin?: boolean;
  screenName?: string;
};

function useContentSubmissionActionsFooterState({
  contentSubmission,
  version,
  isAdmin = false,
  screenName,
}: UseContentSubmissionActionsFooterStateParams) {
  const campaignId = contentSubmission.campaign?.id ?? '';
  const queryClient = useQueryClient();

  const {handleAction, actionComponent} = useContentSubmissionAction({
    campaignId,
    contentSubmissions: [contentSubmission],
    screenName,
    onSuccess: (id) => {
      queryClient.invalidateQueries({
        queryKey: [CONTENT_SUBMISSION_QUERY_KEY, id],
      });
    },
  });

  const {aasmState, kind} = contentSubmission;

  const isAcceptedVideo =
    aasmState === CONTENT_SUBMISSION_STATUS.APPROVED &&
    kind === CONTENT_SUBMISSION_KIND.VIDEO;
  const {remainingResizes, resizeLimit} = usePostProductionGenerations({
    campaignId,
    assetId: contentSubmission.id,
    enabled: isAcceptedVideo,
  });
  const [resizeModal, setResizeModal] = useState<ResizeModalState>('closed');
  const [isCheckingResize, setIsCheckingResize] = useState(false);
  const [sourceDimensions, setSourceDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const resizeCreatorId = contentSubmission.profile?.id;
  const resizeAnalytics = useResizeAnalytics({
    campaignId,
    creatorId: resizeCreatorId,
    flowId: contentSubmission.id,
  });

  const isLatestVersionSelected =
    version.id === contentSubmission.latestVersionId;
  const campaignState =
    contentSubmission.campaign?.aasmState ?? contentSubmission.campaign?.state;
  const isCampaignActive =
    !!campaignState &&
    ACTIVE_CAMPAIGN_STATES.includes(campaignState as CampaignAasmState);

  const allowedActions = aasmState
    ? (CONTENT_SUBMISSION_ACTIONS_BY_STATE[aasmState] ?? [])
    : [];
  const isActionAllowed = (action: ContentSubmissionAction) =>
    isLatestVersionSelected &&
    allowedActions.includes(action) &&
    (isCampaignActive ||
      INACTIVE_CAMPAIGN_CONTENT_SUBMISSION_ACTIONS.includes(action));

  const showApprove = isActionAllowed(CONTENT_SUBMISSION_ACTION.ACCEPT);
  const showRequestEdits = isActionAllowed(CONTENT_SUBMISSION_ACTION.REJECT);
  const showArchive = isActionAllowed(CONTENT_SUBMISSION_ACTION.ARCHIVE);
  const showUnarchive = isActionAllowed(CONTENT_SUBMISSION_ACTION.UNARCHIVE);
  const showShare = allowedActions.includes(CONTENT_SUBMISSION_ACTION.SHARE);
  const showResize =
    isActionAllowed(CONTENT_SUBMISSION_ACTION.RESIZE) &&
    kind === CONTENT_SUBMISSION_KIND.VIDEO;
  // Download is a read-only action — show it whenever we have a URL to download,
  // regardless of workflow state (pending / approved / archived all OK).
  // We accept any of `downloadUrl` (preferred — original file), `version.url`
  // (preview/streaming URL — falls back to HLS .m3u8 for videos), or
  // `contentSubmission.url`. The resolver inside the action hook picks the
  // best one in the same priority order.
  const showDownload = Boolean(
    contentSubmission.downloadUrl || version.url || contentSubmission.url
  );

  const analysisButton = getContentSubmissionAnalysisButtonState({
    isAdmin,
    isLatestVersionSelected,
    analysisStatus: version.contentSubmissionAnalysisStatus,
    isCampaignActive,
    kind,
    aasmState,
  });

  const handleApproveClick = () =>
    handleAction(CONTENT_SUBMISSION_ACTION.ACCEPT, contentSubmission.id);

  const handleRequestEditClick = () =>
    handleAction(CONTENT_SUBMISSION_ACTION.REJECT, contentSubmission.id);

  const handleArchiveButtonClick = () =>
    handleAction(CONTENT_SUBMISSION_ACTION.ARCHIVE, contentSubmission.id);

  const handleUnarchiveButtonClick = () =>
    handleAction(CONTENT_SUBMISSION_ACTION.UNARCHIVE, contentSubmission.id);

  const handleAiAnalysis = () => {
    if (!analysisButton.show) {
      return;
    }
    const action = analysisButton.isViewAction
      ? CONTENT_SUBMISSION_ACTION.VIEW_AI_ANALYSIS
      : CONTENT_SUBMISSION_ACTION.RUN_AI_ANALYSIS;
    handleAction(action, contentSubmission.id, version.id);
  };

  const handleShareClick = () =>
    handleAction(CONTENT_SUBMISSION_ACTION.SHARE, contentSubmission.id);

  const handleResizeClick = async () => {
    if (isCheckingResize) {
      return;
    }
    if (remainingResizes !== undefined && remainingResizes <= 0) {
      resizeAnalytics.trackLimitReached();
      setResizeModal('limit');
      return;
    }
    const sourceUrl =
      version.downloadUrl ?? contentSubmission.downloadUrl ?? version.url;
    setIsCheckingResize(true);
    let eligibility;
    try {
      eligibility = await checkResizeEligibility(sourceUrl);
    } finally {
      setIsCheckingResize(false);
    }
    if (!eligibility.ok) {
      resizeAnalytics.trackIneligible();
      raiseFlashMessageError({
        title: translate("This video can't be resized"),
        subtitle: eligibility.reason,
      });
      return;
    }
    setSourceDimensions(
      eligibility.meta
        ? {width: eligibility.meta.width, height: eligibility.meta.height}
        : null
    );
    setResizeModal('picker');
  };
  const closeResizeModal = () => setResizeModal('closed');

  return {
    showApprove,
    showRequestEdits,
    showArchive,
    showUnarchive,
    showShare,
    showResize,
    showDownload,
    analysisButton,
    actionComponent,
    handleApproveClick,
    handleRequestEditClick,
    handleArchiveButtonClick,
    handleUnarchiveButtonClick,
    handleAiAnalysis,
    handleShareClick,
    handleResizeClick,
    isCheckingResize,
    resizeModal,
    closeResizeModal,
    remainingResizes,
    resizeLimit,
    resizeCampaignId: campaignId,
    resizeAssetId: contentSubmission.id,
    resizeCreatorId,
    resizeSourceWidth: sourceDimensions?.width,
    resizeSourceHeight: sourceDimensions?.height,
    trackResizePlaygroundCta: resizeAnalytics.trackPlaygroundCta,
    trackResizeContactSales: resizeAnalytics.trackContactSales,
  };
}

export default useContentSubmissionActionsFooterState;
